/* Safe Whiskers basket. Shopify is the source of truth for prices and quantities. */
(() => {
  if (customElements.get('sw-basket')) return;
  class SafeWhiskersBasket extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.root = window.Shopify?.routes?.root || '/';
      this.sectionId = this.dataset.section;
      this.lastRemoved = null;
      this.busy = false;
      this.addEventListener('click', event => this.onClick(event));
      this.addEventListener('change', event => this.onChange(event));
      this.addEventListener('submit', event => this.onSubmit(event));
      this.readState();
      this.setupView();
      this.boundPageShow = event => { if (event.persisted) this.sync(); };
      window.addEventListener('pageshow', this.boundPageShow);
      this.boundCartUpdate = event => { if(this.dataset.mode !== 'drawer' && event.detail.source !== this) this.sync(); };
      document.addEventListener('sw:basket-updated',this.boundCartUpdate);
    }
    disconnectedCallback() { this.shippingObserver?.disconnect(); this.observer?.disconnect(); this.summaryObserver?.disconnect(); window.removeEventListener('pageshow', this.boundPageShow); document.removeEventListener('sw:basket-updated',this.boundCartUpdate); }
    readState() { this.cart = JSON.parse(this.querySelector('[data-cart-state]').textContent); }
    money(cents) { return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {style:'currency', currency:this.cart.currency || 'GBP'}).format(cents / 100); }
    endpoint(path) { return this.root + path; }
    async request(path, body, method = 'POST') {
      const response = await fetch(this.endpoint(path), {method, credentials:'same-origin', headers:{Accept:'application/json', ...(body ? {'Content-Type':'application/json'} : {})}, ...(body ? {body:JSON.stringify(body)} : {}), signal:body && /^cart\/(add|change|update)\.js/.test(path) ? undefined : AbortSignal.timeout(20000)});
      const data = await response.json();
      if (!response.ok) {
        const description = data?.description || data?.message || data?.errors || data;
        const message = typeof description === 'string' ? description : Object.entries(description || {}).map(([key,value]) => `${key}: ${[].concat(value).join(', ')}`).join('. ');
        throw new Error(message || 'We could not update your basket. Please try again.');
      }
      return data;
    }
    sectionPayload() { return {sections:[this.sectionId,'cart-icon-bubble'], sections_url:this.dataset.cartUrl}; }
    setBusy(value) {
      this.busy = value;
      this.setAttribute('aria-busy', String(value));
      this.querySelectorAll('button,input,select').forEach(control => {
        if (value) { control.dataset.wasDisabled = String(control.disabled); control.disabled = true; }
        else if ('wasDisabled' in control.dataset) { control.disabled = control.dataset.wasDisabled === 'true'; delete control.dataset.wasDisabled; }
      });
      if (!value) this.checkAvailability();
    }
    message(text, undo = false) {
      this.querySelector('[data-feedback]').textContent = text;
      this.querySelector('.swb-feedback').hidden = !text;
      this.querySelector('[data-undo]').hidden = !undo;
    }
    error(text) { const region=this.querySelector('.swb-error'); region.textContent=text; region.hidden=!text; if(text) region.focus({preventScroll:true}); }
    async render(data, broadcast = true) {
      this.needsRefresh=true;
      let sections=data.sections;
      if (!sections?.[this.sectionId]) {
        const url = new URL(this.dataset.cartUrl, location.origin);
        url.searchParams.set('sections', this.sectionId + ',cart-icon-bubble');
        const response=await fetch(url, {credentials:'same-origin',cache:'no-store'});
        if (!response.ok) throw new Error('The basket changed, but the display could not refresh. Please reload before checking out.');
        sections=await response.json();
      }
      const doc=new DOMParser().parseFromString(sections[this.sectionId] || '', 'text/html');
      const next=doc.querySelector('[data-basket-view]');
      if (!next) throw new Error('Please reload the page to see your latest basket before checking out.');
      this.querySelector('[data-basket-view]').replaceWith(next);
      this.readState();
      this.needsRefresh=false;
      this.querySelector('[data-basket-count]').textContent=this.cart.item_count;
      if (sections['cart-icon-bubble']) {
        const iconDoc=new DOMParser().parseFromString(sections['cart-icon-bubble'],'text/html');
        const bubble=document.getElementById('cart-icon-bubble');
        const replacement=iconDoc.querySelector('.shopify-section');
        if (bubble && replacement) bubble.innerHTML=replacement.innerHTML;
      }
      this.setupView();
      if(broadcast) document.dispatchEvent(new CustomEvent('sw:basket-updated',{detail:{cart:this.cart,source:this}}));
    }
    async sync() {
      if(this.busy) return;
      this.setBusy(true);
      try { await this.render({},false); } catch(error) { this.error(error.message); }
      finally { this.setBusy(false); }
    }
    setupView() {
      this.setupShippingProgress();
      this.checkAvailability();
      this.summaryObserver?.disconnect();
      const summary=this.querySelector('.swb-summary-column');
      if(summary) {
        this.summaryObserver=new ResizeObserver(()=>summary.classList.toggle('swb-summary-tall',summary.offsetHeight > window.innerHeight - 48));
        this.summaryObserver.observe(summary);
      }
      this.observer?.disconnect();
      const checkout=this.querySelector('[data-main-checkout]');
      if(checkout) {
        this.observer=new IntersectionObserver(entries => {
          const bar=this.querySelector('[data-sticky]');
          const checkoutVisible=entries[0].isIntersecting;
          checkout.classList.toggle('swb-checkout-cue',checkoutVisible);
          if(bar) {
            bar.hidden=checkoutVisible;
            bar.querySelector('[name=checkout]')?.classList.toggle('swb-checkout-cue',!checkoutVisible);
          }
        },{threshold:0});
        this.observer.observe(checkout);
      }
    }
    setupShippingProgress() {
      this.shippingObserver?.disconnect();
      const progress=this.querySelector('[data-shipping-progress]');
      if(!progress) { this.shippingProgressState=null; return; }
      const fill=progress.querySelector('.swb-shipping-fill');
      const percent=parseFloat(fill.style.width) || 0;
      const qualified=progress.classList.contains('is-qualified');
      // Wait until the drawer opens or the bar scrolls into view before celebrating.
      this.shippingObserver=new IntersectionObserver(entries=>{
        if(!entries.some(entry=>entry.isIntersecting)) return;
        this.shippingObserver.disconnect();
        const previous=this.shippingProgressState;
        this.shippingProgressState={percent,qualified};
        if(window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        const start=previous?.percent ?? 0;
        if(start!==percent) fill.animate([{width:start+'%'},{width:percent+'%'}],{duration:850,easing:'cubic-bezier(.22,1,.36,1)'});
        if(qualified && !previous?.qualified) progress.classList.add('is-celebrating');
      },{threshold:.5});
      this.shippingObserver.observe(progress);
    }
    checkAvailability() {
      const unavailable=!!this.querySelector('[data-unavailable]');
      this.querySelectorAll('[name=checkout]').forEach(button=>{button.disabled=this.busy||unavailable||this.needsRefresh;});
    }
    async mutate(action, success, focus) {
      if(this.busy) return;
      this.error(''); this.setBusy(true);
      try {
        const result=await action();
        await this.render(result);
        if(success) this.message(success,!!this.lastRemoved);
        if(focus) { const match=[...this.querySelectorAll('.swb-item')].find(item=>item.dataset.key===focus.key); match?.querySelector(focus.selector)?.focus({preventScroll:true}); }
      } catch(error) {
        try { await this.render({}); } catch {}
        this.error(error.name==='TimeoutError' ? 'The connection took too long. Check the latest basket below before trying again.' : error.message);
      } finally { this.setBusy(false); }
    }
    line(element) { const row=element.closest('[data-key]'); return {row, item:this.cart.items.find(item=>item.key===row?.dataset.key)}; }
    onClick(event) {
      const control=event.target.closest('[data-step],[data-remove],[data-undo],[data-save-variant]');
      if(!control) return;
      event.preventDefault();
      if(this.busy) return;
      if(control.hasAttribute('data-undo')) return this.undo();
      const {row,item}=this.line(control);
      if(!item) return this.sync();
      if(control.hasAttribute('data-remove')) return this.remove(item);
      if(control.hasAttribute('data-save-variant')) return this.changeVariant(item, row.querySelector('[data-variant]').value);
      const input=row.querySelector('[data-quantity]');
      const amount=Number(input.value) + Number(control.dataset.step) * Number(input.step || 1);
      input.value=amount;
      this.changeQuantity(item,input);
    }
    onChange(event) {
      if(event.target.matches('[data-quantity]')) { const {item}=this.line(event.target); if(item) this.changeQuantity(item,event.target); }
    }
    onSubmit(event) {
      if(event.target.matches('[data-discount-form]')) { event.preventDefault(); this.discount(event.target); }
      else if(event.target.id.endsWith('BasketForm') && (this.busy || this.needsRefresh)) event.preventDefault();
    }
    changeQuantity(item,input) {
      if(this.busy) return;
      const quantity=Number(input.value);
      if(!Number.isInteger(quantity) || !input.checkValidity()) { input.value=item.quantity; this.error('Please enter a valid quantity. To remove an item, choose Remove.'); return; }
      if(quantity===item.quantity) return;
      this.mutate(()=>this.request('cart/change.js',{id:item.key,quantity,...this.sectionPayload()}),'Basket updated.',{key:item.key,selector:'[data-quantity]'});
    }
    remove(item) {
      this.mutate(async()=>{
        const result=await this.request('cart/change.js',{id:item.key,quantity:0,...this.sectionPayload()});
        this.lastRemoved={id:item.variant_id,quantity:item.quantity,properties:item.properties || {},...(item.selling_plan_allocation ? {selling_plan:item.selling_plan_allocation.selling_plan.id} : {})};
        return result;
      },`${item.product_title} removed.`);
    }
    undo() {
      if(!this.lastRemoved) return;
      // Restore the customer's existing preorder payment choice before app validation.
      if(this.lastRemoved.selling_plan && window._RestockRocketConfig) {
        const config=window._RestockRocketConfig;
        config.variantsSellingPlanId={...(config.variantsSellingPlanId||{}),[this.lastRemoved.id]:this.lastRemoved.selling_plan};
        document.querySelectorAll('.restock-rocket-payment-widget[data-variant-id="'+this.lastRemoved.id+'"] input[type=radio]').forEach(input=>{input.checked=Number(input.value)===Number(this.lastRemoved.selling_plan);});
      }
      this.mutate(async()=>{ const result=await this.request('cart/add.js',{items:[this.lastRemoved],...this.sectionPayload()}); this.lastRemoved=null; return result; },'Item restored to your basket.');
    }
    changeVariant(item,variantId) {
      if(Number(variantId)===item.variant_id) return;
      this.mutate(async()=>{
        // Keep the original line until Shopify has accepted the replacement.
        const fresh=await this.request('cart.js',null,'GET');
        const original=fresh.items.find(line=>line.key===item.key);
        if(!original) throw new Error('Your basket changed in another tab. Please review it and try again.');
        const addition=await this.request('cart/add.js',{items:[{id:Number(variantId),quantity:original.quantity,properties:original.properties || {}}]});
        const added=addition.items[0];
        try {
          const current=await this.request('cart.js',null,'GET');
          const existing=current.items.find(line=>line.variant_id===original.variant_id && JSON.stringify(line.properties||{})===JSON.stringify(original.properties||{}) && (line.selling_plan_allocation?.selling_plan?.id||null)===(original.selling_plan_allocation?.selling_plan?.id||null));
          if(!existing) throw new Error('The original item changed.');
          return await this.request('cart/change.js',{id:existing.key,quantity:0,...this.sectionPayload()});
        } catch(error) {
          // Never repeat the add after an uncertain response. Reconcile what remains.
          const current=await this.request('cart.js',null,'GET');
          const oldStillPresent=current.items.some(line=>line.variant_id===original.variant_id && JSON.stringify(line.properties||{})===JSON.stringify(original.properties||{}));
          if(!oldStillPresent) return {};
          const newLine=current.items.find(line=>line.key===added.key) || current.items.find(line=>line.variant_id===Number(variantId) && JSON.stringify(line.properties||{})===JSON.stringify(original.properties||{}));
          if(newLine) await this.request('cart/change.js',{id:newLine.key,quantity:Math.max(0,newLine.quantity-original.quantity)});
          throw new Error('We could not change this option. Your original selection has been kept; please check your basket and try again.');
        }
      },'Product options updated.');
    }
    async discount(form) {
      if(this.busy) return;
      const code=form.elements.discount.value.trim();
      if(!code) return;
      let applicable=false;
      await this.mutate(async()=>{
        const existing=(this.cart.discount_codes||[]).filter(value=>value.applicable).map(value=>value.code);
        const codes=[...new Set([...existing,code])];
        const result=await this.request('cart/update.js',{discount:codes.join(','),...this.sectionPayload()});
        applicable=!!result.discount_codes?.some(value=>value.code.toLowerCase()===code.toLowerCase()&&value.applicable);
        return result;
      },'');
      const details=this.querySelector('.swb-coupon');
      if(details) details.open=true;
      const message=this.querySelector('[data-discount-message]');
      if(message) message.textContent=applicable ? 'Discount applied. Any delivery discount is confirmed with your delivery options at checkout.' : 'This code has not applied to the basket. Check the code and its conditions; some offers are confirmed at checkout.';
    }
  }
  customElements.define('sw-basket', SafeWhiskersBasket);
})();
